import './PracticeIntroVideoFullScreen.scss'

import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import {
  saveUserEvent,
  EVENT_CATEGORY_PRACTICES,
  EVENT_ACTION_PAGEVIEW
} from '../../common/api/UserEvents'
import { getPracticeDetails } from '../../common/api/Practices'
import { Row, Col, Container, Spinner, Modal, Button } from 'react-bootstrap'
import parse from 'html-react-parser'
import LessonVideo from 'components/lessons/LessonVideo'
import ChatCTA from 'components/chat/ChatCTA/ChatCTA'
import {
  ROUTE_CHAT_GENERAL,
  ROUTE_TODAY
} from 'components/common/helpers/Paths'

/**
 * Practice Intro Video component. Used for displaying a "why" video within a practice.
 */
export default function PracticeIntroVideoFullScreen({
  setReturnPath,
  setHeaderTitle
}) {
  const { practiceType } = useParams()
  const { search } = useLocation()

  // Did we redirect with a return path from an auth-required page?
  const searchParams = new URLSearchParams(search)
  const returnPath = searchParams.get('returnPath') || ROUTE_TODAY

  const [videoUrl, setVideoUrl] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [practice, setPractice] = useState()
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    if (!practiceType) return

    const practice = getPracticeDetails(practiceType)

    // If no result, abort
    if (!practice) return

    setPractice(practice)
    setVideoUrl(practice.why.videoURL)

    // Clear header title
    setHeaderTitle(null)
    setReturnPath(returnPath)

    // Log access
    saveUserEvent(
      EVENT_CATEGORY_PRACTICES,
      EVENT_ACTION_PAGEVIEW,
      `why_${practiceType}`
    )
    setIsLoading(false)
  }, [practiceType, setHeaderTitle, setReturnPath, returnPath])

  const handleModalClose = () => {
    setShowPreview(false)
  }

  return !practiceType || !videoUrl ? null : (
    <>
      <Container fluid className={`practice-intro-detail-container`}>
        {isLoading || !practice ? (
          <Row className='spinner-wrapper'>
            <Spinner
              animation='border'
              role='status'
              className='app-spinner'
              variant='primary'
              size='lg'
            >
              <span className='visually-hidden'>Loading...</span>
            </Spinner>
          </Row>
        ) : (
          <>
            <Row className='practice-intro-intro'>
              <div className='intro-content'>
                <h1>{parse(practice.title)}</h1>
              </div>
            </Row>
            <div
              className='practice-intro-preview'
              style={{
                backgroundImage: `url(${practice.image})`
              }}
            ></div>

            <div className='practice-intro-section'>
              <div className='practice-intro-wrapper'>
                <div className='author-container'>
                  <div className='author-bottom'>
                    <Row className='practice-intros-content-header'>
                      <Col>
                        <h4>{practice.why.subtitle}</h4>
                        <p>
                          <ChatCTA
                            asLink={true}
                            message="Get Mia's help"
                            uri={`${ROUTE_CHAT_GENERAL}/create?returnPath=${returnPath}`}
                          />
                        </p>
                        <span
                          className='practice-intros-content-time'
                          hidden={!practice.why.video_duration}
                        >
                          {practice.why.video_duration} mins
                        </span>

                        <div className='practice-intros-content-text'>
                          {parse(practice.why.content)}
                        </div>
                      </Col>
                    </Row>
                    <Row className='button'>
                      <Button
                        size='lg'
                        variant='primary'
                        className='w-100 mt-3'
                        onClick={() => setShowPreview(true)}
                      >
                        <span>Watch</span>
                      </Button>
                    </Row>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Container>

      {/* Preview Modal, desktop only */}
      {practice && (
        <Modal
          show={showPreview}
          onHide={handleModalClose}
          dialogClassName='modal-practice-intro-preview'
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {parse(practice.title)} Introduction Video
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <LessonVideo
              video={{
                id: 0,
                acf_extended: {
                  // TODO: Add stream_url to video
                  video_url: practice.why.videoURL,
                  captions_url: practice.why.captionsURL
                }
              }}
              practiceType={practiceType}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}
